/*
 * Critical Design
 * Do not modify this section without explicit permission
 * 
 * Airport detail page that handles:
 * - Display and editing of airport information
 * - Service provider management
 * - Operational considerations
 * - Offline-capable editing
 * - Special use airport styling
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import { Wifi, WifiOff } from 'lucide-react';
import { useLayout } from '../contexts/LayoutContext';
import { useNetwork } from '../contexts/NetworkContext';
import { airportService } from '../services/airportService';
import AirportDetailContainer from '../components/AirportDetailContainer';
import AirportEditForm from '../components/AirportEditForm';
import { useAuth } from '../contexts/AuthContext';
import BaseMaps from '../components/BaseMaps';
import { uploadBaseMap, deleteBaseMap } from '../services/baseMapService';

const PencilIcon = () => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    className="w-5 h-5"
  >
    <path 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      strokeWidth="2" 
      d="M15.232 5.232l1.687-1.688a1.875 1.875 0 112.652 2.652L6.5 21h-3v-3L16.732 3.732z" 
    />
  </svg>
);

const ServiceEditForm = ({ service, onSave, onCancel, onDelete }) => {
  const [editedService, setEditedService] = useState(service);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleChange = (field, value) => {
    setEditedService(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleServicesChange = (index, value) => {
    const newServices = [...editedService.services];
    newServices[index] = value;
    handleChange('services', newServices);
  };

  const handleCancel = () => {
    if (showDeleteConfirm) {
      setShowDeleteConfirm(false);
    } else {
      onCancel();
    }
  };

  const addService = () => {
    handleChange('services', [...editedService.services, '']);
  };

  const removeService = (index) => {
    const newServices = editedService.services.filter((_, i) => i !== index);
    handleChange('services', newServices);
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block font-medium mb-1 text-primary dark:text-primary-dark">
          Name
        </label>
        <input
          type="text"
          value={editedService.name}
          onChange={(e) => handleChange('name', e.target.value)}
          className="w-full p-2 rounded-md bg-surface dark:bg-surface-dark text-primary dark:text-primary-dark border-default dark:border-default-dark"
        />
      </div>
      <div>
        <label className="block font-medium mb-1 text-primary dark:text-primary-dark">
          Phone
        </label>
        <input
          type="text"
          value={editedService.phone}
          onChange={(e) => handleChange('phone', e.target.value)}
          className="w-full p-2 rounded-md bg-surface dark:bg-surface-dark text-primary dark:text-primary-dark border-default dark:border-default-dark"
        />
      </div>
      <div>
        <label className="block font-medium mb-1 text-primary dark:text-primary-dark">
          After Hours Phone
        </label>
        <input
          type="text"
          value={editedService.afterHoursPhone || ''}
          onChange={(e) => handleChange('afterHoursPhone', e.target.value)}
          className="w-full p-2 rounded-md bg-surface dark:bg-surface-dark text-primary dark:text-primary-dark border-default dark:border-default-dark"
        />
      </div>
      <div>
        <label className="block font-medium mb-1 text-primary dark:text-primary-dark">
          Hours
        </label>
        <input
          type="text"
          value={editedService.hours}
          onChange={(e) => handleChange('hours', e.target.value)}
          className="w-full p-2 rounded-md bg-surface dark:bg-surface-dark text-primary dark:text-primary-dark border-default dark:border-default-dark"
        />
      </div>
      <div>
        <label className="block font-medium mb-1 text-primary dark:text-primary-dark">
          Contact Person
        </label>
        <input
          type="text"
          value={editedService.contactPerson || ''}
          onChange={(e) => handleChange('contactPerson', e.target.value)}
          className="w-full p-2 rounded-md bg-surface dark:bg-surface-dark text-primary dark:text-primary-dark border-default dark:border-default-dark"
        />
      </div>
      <div>
        <label className="block font-medium mb-1 text-primary dark:text-primary-dark">
          Website
        </label>
        <input
          type="text"
          value={editedService.website || ''}
          onChange={(e) => handleChange('website', e.target.value)}
          className="w-full p-2 rounded-md bg-surface dark:bg-surface-dark text-primary dark:text-primary-dark border-default dark:border-default-dark"
        />
      </div>
      <div>
        <label className="block font-medium mb-1 text-primary dark:text-primary-dark">
          Services
        </label>
        <div className="space-y-2">
          {editedService.services.map((item, index) => (
            <div key={index} className="flex gap-2">
              <input
                type="text"
                value={item}
                onChange={(e) => handleServicesChange(index, e.target.value)}
                className="flex-1 p-2 rounded-md bg-surface dark:bg-surface-dark text-primary dark:text-primary-dark border-default dark:border-default-dark"
              />
              <button
                onClick={() => removeService(index)}
                className="p-2 text-status-error hover:text-status-error-hover dark:text-status-error-dark dark:hover:text-status-error-hover-dark"
              >
                Remove
              </button>
            </div>
          ))}
          <button
            onClick={addService}
            className="text-link hover:text-link-hover dark:text-link-dark dark:hover:text-link-hover-dark"
          >
            + Add Service
          </button>
        </div>
      </div>
      <div>
        <label className="block font-medium mb-1 text-primary dark:text-primary-dark">
          Notes
        </label>
        <textarea
          value={editedService.notes || ''}
          onChange={(e) => handleChange('notes', e.target.value)}
          rows="4"
          className="w-full p-2 rounded-md bg-surface dark:bg-surface-dark text-primary dark:text-primary-dark border-default dark:border-default-dark"
        />
      </div>
      
      {showDeleteConfirm ? (
        <div className="p-4 border rounded-md bg-status-error-bg border-status-error text-black">
          <p className="mb-4">Are you sure you want to delete this company? This action cannot be undone.</p>
          <div className="flex justify-end gap-2">
            <button
              onClick={handleCancel}
              className="px-4 py-2 bg-surface text-black rounded-md hover:bg-surface-hover"
            >
              Cancel
            </button>
            <button
              onClick={onDelete}
              className="px-4 py-2 bg-status-error-text text-white rounded-md hover:bg-status-error-text/90"
            >
              Delete
            </button>
          </div>
        </div>
      ) : (
        <div className="flex justify-between gap-2">
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="px-4 py-2 text-red-600 hover:text-red-700 dark:text-red-300 dark:hover:text-red-200"
          >
            Delete Company
          </button>
          <div className="flex gap-2">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-secondary hover:text-secondary-hover dark:text-secondary-dark dark:hover:text-secondary-hover-dark"
            >
              Cancel
            </button>
            <button
              onClick={() => onSave({ ...editedService, dateModified: new Date().toISOString() })}
              className="px-4 py-2 bg-button text-white rounded-md hover:bg-button-hover dark:bg-button-dark dark:hover:bg-button-hover-dark"
            >
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const ServiceCard = ({ service, onEdit }) => {
  const { currentUser } = useAuth();
  
  return (
    <div className="bg-surface dark:bg-surface-dark rounded-lg shadow-md p-4 mb-4 relative">
      {currentUser?.profile?.isEditor && (
        <button
          onClick={() => onEdit(service)}
          className="absolute right-4 top-4 p-2 text-secondary hover:text-link dark:text-secondary-dark dark:hover:text-link-dark rounded-lg hover:bg-surface-hover dark:hover:bg-surface-hover-dark transition-colors"
          title="Edit service"
        >
          <PencilIcon />
        </button>
      )}
      <h3 className="text-lg font-bold mb-2 text-primary dark:text-primary-dark">
        {service.name}
      </h3>
      <div className="space-y-2">
        <p className="text-primary dark:text-primary-dark">
          <span className="font-medium">Phone:</span>{' '}
          <a 
            href={`tel:${service.phone.replace(/[^0-9]/g, '')}`}
            className="text-phone-link dark:text-phone-link-dark"
          >
            {service.phone}
          </a>
        </p>
        {service.afterHoursPhone && (
          <p className="text-primary dark:text-primary-dark">
            <span className="font-medium">After Hours:</span>{' '}
            <a 
              href={`tel:${service.afterHoursPhone.replace(/[^0-9]/g, '')}`}
              className="text-phone-link dark:text-phone-link-dark"
            >
              {service.afterHoursPhone}
            </a>
          </p>
        )}
        <p className="text-primary dark:text-primary-dark">
          <span className="font-medium">Hours:</span> {service.hours}
        </p>
        {service.contactPerson && (
          <p className="text-primary dark:text-primary-dark">
            <span className="font-medium">Contact:</span> {service.contactPerson}
          </p>
        )}
        {service.website && (
          <a 
            href={service.website.startsWith('http') ? service.website : `https://${service.website}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-phone-link dark:text-phone-link-dark"
          >
            Website
          </a>
        )}
        <div className="mt-3">
          <p className="font-medium mb-1 text-primary dark:text-primary-dark">
            Services:
          </p>
          <ul className="list-disc list-inside pl-2">
            {service.services.map((item, index) => (
              <li key={index} className="text-primary dark:text-primary-dark">
                {item}
              </li>
            ))}
          </ul>
        </div>
        {service.notes && (
          <div className="mt-3">
            <p className="font-medium mb-1 text-primary dark:text-primary-dark">
              Notes:
            </p>
            <p className="whitespace-pre-wrap text-primary dark:text-primary-dark">
              {service.notes}
            </p>
          </div>
        )}
        <div className="mt-4 text-sm text-secondary dark:text-secondary-dark">
          Last modified: {new Date(service.dateModified).toLocaleString('en-US', {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric'
          })}
        </div>
      </div>
    </div>
  );
};

const AirportDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isOnline, isSyncing } = useNetwork();
  const { currentUser } = useAuth();
  const [isEditingOps, setIsEditingOps] = useState(false);
  const [isEditingAirport, setIsEditingAirport] = useState(id === 'new');
  const [editingService, setEditingService] = useState(null);
  const [isEditingSecret, setIsEditingSecret] = useState(false);
  const [isEditingDogs, setIsEditingDogs] = useState(false);
  const [isEditingLogistics, setIsEditingLogistics] = useState(false);
  const [airport, setAirport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAirport = async () => {
      try {
        if (id === 'new') {
          setAirport({
            id: '',
            airportCode: '',
            airportName: '',
            city: '',
            state: '',
            elevation: '',
            atisNumber: '',
            awosNumber: '',
            operationalConsiderations: '',
            secretInformation: '',
            services: [],
            tags: [],
            dateModified: new Date().toISOString(),
            lastModified: new Date().toISOString(),
          });
          setLoading(false);
          return;
        }

        const data = await airportService.getAirportById(id);
        if (data) {
          setAirport({
            ...data,
            services: data.services || [],
            tags: data.tags || [],
          });
        } else {
          console.error('Airport not found');
          setAirport(null);
        }
      } catch (error) {
        console.error('Error fetching airport:', error);
        setAirport(null);
      } finally {
        setLoading(false);
      }
    };

    fetchAirport();
  }, [id]);

  const handleSave = async (updatedData) => {
    try {
      const timestamp = new Date().toISOString();
      const dataToSave = {
        id: updatedData.airportCode,
        airportCode: updatedData.airportCode,
        ...updatedData,
        services: updatedData.services || [],
        tags: updatedData.tags || [],
        dateModified: timestamp,
        lastModified: timestamp,
        operationalLastModified: timestamp
      };
      
      // Update local state first
      const updatedAirport = {
        ...updatedData,
        id: dataToSave.airportCode,
        services: dataToSave.services || [],
        tags: dataToSave.tags || [],
        dateModified: timestamp,
        lastModified: timestamp,
        operationalLastModified: timestamp
      };
      
      setAirport(updatedAirport);
      setIsEditingAirport(false);
      
      // Then attempt to save to Firestore
      await airportService.saveAirport(dataToSave);
      
      // If this was a new airport, navigate to its permanent URL
      if (id === 'new') {
        navigate(`/airport/${dataToSave.airportCode}`, { replace: true });
      }
    } catch (error) {
      console.error('Error saving airport:', error);
      // Even if save fails, we keep the UI updated since changes will sync later
    }
  };

  const handleDelete = async () => {
    try {
      // Attempt to delete from Firestore first
      await airportService.deleteAirport(id);
      // If successful, navigate back to the list
      navigate('/');
    } catch (error) {
      console.error('Error deleting airport:', error);
      // You might want to show an error message to the user here
    }
  };

  const handleSaveOperational = async (newText) => {
    try {
      const timestamp = new Date().toISOString();
      // Update local state first
      setAirport(prev => ({
        ...prev,
        operationalConsiderations: newText,
        operationalLastModified: timestamp,
        dateModified: timestamp,
        lastModified: timestamp
      }));
      setIsEditingOps(false);

      // Then attempt to save to Firestore
      await airportService.updateOperationalConsiderations(id, newText, timestamp);
    } catch (error) {
      console.error('Error saving operational considerations:', error);
      // Even if save fails, we keep the UI updated since changes will sync later
    }
  };

  const handleSaveDogs = async (newText) => {
    try {
      const timestamp = new Date().toISOString();
      // Update local state first
      setAirport(prev => ({
        ...prev,
        dogsAndFun: newText,
        dogsLastModified: timestamp,
        dateModified: timestamp,
        lastModified: timestamp
      }));
      setIsEditingDogs(false);

      // Then attempt to save to Firestore
      await airportService.updateDogsAndFun(id, newText, timestamp);
    } catch (error) {
      console.error('Error saving dogs and fun information:', error);
      // Even if save fails, we keep the UI updated since changes will sync later
    }
  };

  const handleSaveLogistics = async (newText) => {
    try {
      const timestamp = new Date().toISOString();
      // Update local state first
      setAirport(prev => ({
        ...prev,
        groundTimeAndLogistics: newText,
        logisticsLastModified: timestamp,
        dateModified: timestamp,
        lastModified: timestamp
      }));
      setIsEditingLogistics(false);

      // Then attempt to save to Firestore
      await airportService.updateGroundTimeAndLogistics(id, newText, timestamp);
    } catch (error) {
      console.error('Error saving ground time and logistics information:', error);
      // Even if save fails, we keep the UI updated since changes will sync later
    }
  };

  const handleSaveSecret = async (newText) => {
    try {
      const timestamp = new Date().toISOString();
      // Update local state first
      setAirport(prev => ({
        ...prev,
        secretInformation: newText,
        secretLastModified: timestamp,
        dateModified: timestamp,
        lastModified: timestamp
      }));
      setIsEditingSecret(false);

      // Then attempt to save to Firestore
      await airportService.updateSecretInformation(id, newText, timestamp);
    } catch (error) {
      console.error('Error saving secret information:', error);
      // Even if save fails, we keep the UI updated since changes will sync later
    }
  };

  const handleSaveService = async (updatedService) => {
    try {
      // Update local state first
      let updatedServices;
      
      if (updatedService.name === '') {
        // This is a new service
        updatedServices = [
          ...airport.services,
          {
            ...updatedService,
            name: `Service ${airport.services.length + 1}`,
            dateModified: new Date().toISOString()
          }
        ];
      } else if (editingService.name === '') {
        // This is saving a new service with a custom name
        updatedServices = [
          ...airport.services,
          {
            ...updatedService,
            dateModified: new Date().toISOString()
          }
        ];
      } else {
        // This is updating an existing service
        updatedServices = airport.services.map(service => 
          service === editingService ? {
            ...updatedService,
            dateModified: new Date().toISOString()
          } : service
        );
      }

      setAirport(prev => ({
        ...prev,
        services: updatedServices
      }));
      setEditingService(null);

      // Then attempt to save to Firestore
      await airportService.updateServices(id, updatedServices);
    } catch (error) {
      console.error('Error saving service:', error);
      // Even if save fails, we keep the UI updated since changes will sync later
    }
  };

  const handleDeleteService = async (serviceName) => {
    try {
      // Update local state first
      const updatedServices = airport.services.filter(service => service.name !== serviceName);
      setAirport(prev => ({
        ...prev,
        services: updatedServices
      }));
      setEditingService(null);

      // Then attempt to save to Firestore
      await airportService.updateServices(id, updatedServices);
    } catch (error) {
      console.error('Error deleting service:', error);
      // Even if delete fails, we keep the UI updated since changes will sync later
    }
  };

  // Add helper function at the top of the component to check for special tags
  const hasSpecialTag = (airport) => {
    if (!airport || !airport.tags) return false;
    const tags = airport.tags.map(tag => tag.toLowerCase().replace(/#/g, ''));
    return tags.includes('specialuse') || tags.includes('nightrestricted');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-surface dark:bg-surface-dark text-secondary dark:text-secondary-dark">
        Loading airport details...
      </div>
    );
  }

  if (!airport) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-page dark:bg-page-dark text-secondary dark:text-secondary-dark">
        <p className="mb-4">Airport not found</p>
        <button
          onClick={() => navigate('/')}
          className="flex items-center text-link dark:text-link-dark hover:text-link-hover dark:hover:text-link-hover-dark"
        >
          <ChevronLeftIcon className="h-6 w-6 mr-1" />
          Back to Airport List
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-page dark:bg-page-dark">
      {/* Network Status Bar */}
      {(isSyncing || !isOnline) && (
        <div className={`
          flex items-center justify-center py-1 px-4 text-sm
          ${isOnline 
            ? 'bg-status-network-online-bg dark:bg-status-network-online-bg-dark text-status-network-online-text dark:text-status-network-online-text-dark'
            : 'bg-status-network-offline-bg dark:bg-status-network-offline-bg-dark text-status-network-offline-text dark:text-status-network-offline-text-dark'
          }
          transition-all duration-300
          ${!isOnline ? 'h-8' : 'h-4'}
        `}>
          {!isOnline ? (
            <>
              <WifiOff className="w-4 h-4 mr-2" />
              Offline Mode - Changes will sync when back online
            </>
          ) : isSyncing ? (
            <div className="flex items-center text-xs">
              <Wifi className="w-3 h-3 mr-1" />
              Syncing changes...
            </div>
          ) : null}
        </div>
      )}

      {/* Back Button - Always visible */}
      <div className={`sticky top-0 z-10 ${
        hasSpecialTag(airport) 
          ? 'bg-special-airport dark:bg-special-airport-dark'
          : 'bg-surface dark:bg-surface-dark'
      }`}>
        <div className="flex items-center p-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-secondary dark:text-secondary-dark hover:text-primary dark:hover:text-primary-dark"
          >
            <ChevronLeftIcon className="h-6 w-6 mr-1" />
            Back
          </button>
          <div className="flex-1 flex justify-center items-center">
            <div className="font-['Fira_Code'] text-xl font-bold text-primary dark:text-primary-dark">
              [{airport.id}]
            </div>
          </div>
          <button
            onClick={() => setIsEditingAirport(true)}
            className={`
              p-2 rounded-lg
              text-secondary hover:text-link dark:text-secondary-dark dark:hover:text-link-dark
              hover:bg-surface-hover dark:hover:bg-surface-hover-dark
              transition-colors
              ${!currentUser?.profile?.isEditor ? 'hidden' : ''}
            `}
            title="Edit airport"
          >
            <PencilIcon />
          </button>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className={`p-4 ${
          hasSpecialTag(airport) 
            ? 'bg-special-airport dark:bg-special-airport-dark'
            : 'bg-surface dark:bg-surface-dark'
        }`}>
          {isEditingAirport ? (
            <AirportEditForm
              airport={airport}
              onSave={handleSave}
              onCancel={() => setIsEditingAirport(false)}
              onDelete={handleDelete}
            />
          ) : (
            <div>
              <div>
                <h1 className="text-lg text-primary dark:text-primary-dark">
                  {airport.airportName}
                </h1>
                <p className="text-secondary dark:text-secondary-dark">
                  {airport.city}, {airport.state} • {airport.elevation} ft
                </p>
                <div className="flex gap-4">
                  <span className="text-secondary dark:text-secondary-dark">
                    ATIS: <a href={`tel:${airport.atisNumber?.replace(/[^0-9]/g, '')}`} className="text-phone-link dark:text-phone-link-dark">{airport.atisNumber}</a>
                  </span>
                  <span className="text-secondary dark:text-secondary-dark">
                    AWOS: <a href={`tel:${airport.awosNumber?.replace(/[^0-9]/g, '')}`} className="text-phone-link dark:text-phone-link-dark">{airport.awosNumber}</a>
                  </span>
                </div>
                
                {/* Tags display */}
                {airport.tags && airport.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {airport.tags.map((tag, index) => (
                      <span 
                        key={index}
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-button/20 text-button dark:bg-button-dark/30 dark:text-button-dark"
                      >
                        #{tag.replace(/#/g, '')}
                      </span>
                    ))}
                  </div>
                )}
                
                <p className="mt-1 text-secondary dark:text-secondary-dark text-sm">
                  Last modified: {new Date(airport.lastModified || airport.dateModified).toLocaleString('en-US', {
                    year: 'numeric',
                    month: 'numeric',
                    day: 'numeric'
                  })}
                </p>
              </div>
            </div>
          )}
        </div>

        {!isEditingAirport && (
          <div className="p-4 space-y-6 pb-24">
            {/* Operational Considerations */}
            <section>
              <div className="flex justify-between items-center mb-3">
                <h2 className="text-xl font-bold text-primary dark:text-primary-dark">
                  Operational Considerations
                </h2>
                <button
                  onClick={() => setIsEditingOps(true)}
                  className={`
                    p-2 rounded-lg
                    text-tertiary hover:text-link dark:text-tertiary-dark dark:hover:text-link-dark
                    hover:bg-surface-hover dark:hover:bg-surface-hover-dark
                    transition-colors
                    ${!currentUser?.profile?.isEditor ? 'hidden' : ''}
                  `}
                  title="Edit operational considerations"
                >
                  <PencilIcon />
                </button>
              </div>
              <div className="bg-surface dark:bg-surface-dark rounded-lg shadow-surface dark:shadow-surface-dark p-4">
                {isEditingOps ? (
                  <div className="space-y-4">
                    <textarea
                      value={airport.operationalConsiderations}
                      onChange={(e) => setAirport(prev => ({
                        ...prev,
                        operationalConsiderations: e.target.value
                      }))}
                      rows="15"
                      className="w-full p-2 rounded-md bg-surface dark:bg-surface-dark text-primary dark:text-primary-dark border-default dark:border-default-dark"
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setIsEditingOps(false)}
                        className="px-4 py-2 text-secondary hover:text-secondary-hover dark:text-secondary-dark dark:hover:text-secondary-hover-dark"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleSaveOperational(airport.operationalConsiderations)}
                        className="px-4 py-2 bg-button text-white rounded-md hover:bg-button-hover dark:bg-button-dark dark:hover:bg-button-hover-dark"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <pre className="whitespace-pre-wrap font-sans text-primary dark:text-primary-dark">
                      {airport.operationalConsiderations}
                    </pre>
                    <div className="mt-4 text-sm text-secondary dark:text-secondary-dark">
                      Last modified: {new Date(airport.operationalLastModified || airport.dateModified).toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'numeric',
                        day: 'numeric'
                      })}
                    </div>
                  </>
                )}
              </div>
            </section>

            {/* Dogs and Fun Section */}
            <section>
              <div className="flex justify-between items-center mb-3">
                <h2 className="text-xl font-bold text-primary dark:text-primary-dark">
                  Dogs and Fun
                </h2>
                <button
                  onClick={() => setIsEditingDogs(true)}
                  className={`
                    p-2 rounded-lg
                    text-tertiary hover:text-link dark:text-tertiary-dark dark:hover:text-link-dark
                    hover:bg-surface-hover dark:hover:bg-surface-hover-dark
                    transition-colors
                    ${!currentUser?.profile?.isEditor ? 'hidden' : ''}
                  `}
                  title="Edit dogs and fun information"
                >
                  <PencilIcon />
                </button>
              </div>
              <div className="bg-surface dark:bg-surface-dark rounded-lg shadow-surface dark:shadow-surface-dark p-4">
                {isEditingDogs ? (
                  <div className="space-y-4">
                    <textarea
                      value={airport.dogsAndFun || ''}
                      onChange={(e) => setAirport(prev => ({
                        ...prev,
                        dogsAndFun: e.target.value
                      }))}
                      rows="15"
                      className="w-full p-2 rounded-md bg-surface dark:bg-surface-dark text-primary dark:text-primary-dark border-default dark:border-default-dark"
                      placeholder="Enter information about dogs and fun activities at this airport..."
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setIsEditingDogs(false)}
                        className="px-4 py-2 text-secondary hover:text-secondary-hover dark:text-secondary-dark dark:hover:text-secondary-hover-dark"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleSaveDogs(airport.dogsAndFun)}
                        className="px-4 py-2 bg-button text-white rounded-md hover:bg-button-hover dark:bg-button-dark dark:hover:bg-button-hover-dark"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <pre className="whitespace-pre-wrap font-sans text-primary dark:text-primary-dark">
                      {airport.dogsAndFun}
                    </pre>
                    <div className="mt-4 text-sm text-secondary dark:text-secondary-dark">
                      Last modified: {new Date(airport.dogsLastModified || airport.dateModified).toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'numeric',
                        day: 'numeric'
                      })}
                    </div>
                  </>
                )}
              </div>
            </section>

            {/* Ground Time and Logistics Section */}
            <section className={`${(!currentUser?.profile?.isEditor && !currentUser?.profile?.viewSecret) ? 'hidden' : ''}`}>
              <div className="flex justify-between items-center mb-3">
                <h2 className="text-xl font-bold text-primary dark:text-primary-dark">
                  Ground Time and Logistics
                </h2>
                <button
                  onClick={() => setIsEditingLogistics(true)}
                  className={`
                    p-2 rounded-lg
                    text-tertiary hover:text-link dark:text-tertiary-dark dark:hover:text-link-dark
                    hover:bg-surface-hover dark:hover:bg-surface-hover-dark
                    transition-colors
                    ${!currentUser?.profile?.isEditor ? 'hidden' : ''}
                  `}
                  title="Edit ground time and logistics information"
                >
                  <PencilIcon />
                </button>
              </div>
              <div className="bg-surface dark:bg-surface-dark rounded-lg shadow-surface dark:shadow-surface-dark p-4">
                {isEditingLogistics ? (
                  <div className="space-y-4">
                    <textarea
                      value={airport.groundTimeAndLogistics || ''}
                      onChange={(e) => setAirport(prev => ({
                        ...prev,
                        groundTimeAndLogistics: e.target.value
                      }))}
                      rows="15"
                      className="w-full p-2 rounded-md bg-surface dark:bg-surface-dark text-primary dark:text-primary-dark border-default dark:border-default-dark"
                      placeholder="Enter information about ground time and logistics at this airport..."
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setIsEditingLogistics(false)}
                        className="px-4 py-2 text-secondary hover:text-secondary-hover dark:text-secondary-dark dark:hover:text-secondary-hover-dark"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleSaveLogistics(airport.groundTimeAndLogistics)}
                        className="px-4 py-2 bg-button text-white rounded-md hover:bg-button-hover dark:bg-button-dark dark:hover:bg-button-hover-dark"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <pre className="whitespace-pre-wrap font-sans text-primary dark:text-primary-dark">
                      {airport.groundTimeAndLogistics}
                    </pre>
                    <div className="mt-4 text-sm text-secondary dark:text-secondary-dark">
                      Last modified: {new Date(airport.logisticsLastModified || airport.dateModified).toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'numeric',
                        day: 'numeric'
                      })}
                    </div>
                  </>
                )}
              </div>
            </section>

            {/* Base Maps Section */}
            <BaseMaps
              airportId={airport.id}
              baseMaps={airport.baseMaps || []}
              onUpload={async ({ file, title }) => {
                try {
                  const baseMap = await uploadBaseMap(airport.id, file, title);
                  await airportService.addBaseMap(airport.id, baseMap);
                  // Update local state
                  setAirport(prev => ({
                    ...prev,
                    baseMaps: [...(prev.baseMaps || []), baseMap]
                  }));
                } catch (error) {
                  console.error('Error uploading base map:', error);
                  throw error;
                }
              }}
              onDelete={async (file) => {
                try {
                  await deleteBaseMap(airport.id, file);
                  await airportService.deleteBaseMap(airport.id, file.id);
                  // Update local state
                  setAirport(prev => ({
                    ...prev,
                    baseMaps: (prev.baseMaps || []).filter(map => map.id !== file.id)
                  }));
                } catch (error) {
                  console.error('Error deleting base map:', error);
                  throw error;
                }
              }}
              onTitleEdit={async (updatedFile) => {
                try {
                  // Create the updated baseMaps array
                  const updatedBaseMaps = (airport.baseMaps || []).map(map => 
                    map.id === updatedFile.id ? { ...map, title: updatedFile.title } : map
                  );
                  
                  // Update local state first
                  setAirport(prev => ({
                    ...prev,
                    baseMaps: updatedBaseMaps
                  }));
                  
                  // Then update in Firestore using the updated array
                  await airportService.updateBaseMaps(airport.id, updatedBaseMaps);
                } catch (error) {
                  console.error('Error updating base map title:', error);
                  throw error;
                }
              }}
            />

            {/* Secret Information */}
            <section className={`${(!currentUser?.profile?.isEditor && !currentUser?.profile?.viewSecret) ? 'hidden' : ''}`}>
              <div className="flex justify-between items-center mb-3">
                <h2 className="text-xl font-bold text-primary dark:text-primary-dark">
                  Secret Information
                </h2>
                <button
                  onClick={() => setIsEditingSecret(true)}
                  className={`
                    p-2 rounded-lg
                    text-tertiary hover:text-link dark:text-tertiary-dark dark:hover:text-link-dark
                    hover:bg-surface-hover dark:hover:bg-surface-hover-dark
                    transition-colors
                    ${!currentUser?.profile?.isEditor ? 'hidden' : ''}
                  `}
                  title="Edit secret information"
                >
                  <PencilIcon />
                </button>
              </div>
              <div className="bg-surface dark:bg-surface-dark rounded-lg shadow-surface dark:shadow-surface-dark p-4">
                {isEditingSecret ? (
                  <div className="space-y-4">
                    <textarea
                      value={airport.secretInformation}
                      onChange={(e) => setAirport(prev => ({
                        ...prev,
                        secretInformation: e.target.value
                      }))}
                      rows="15"
                      className="w-full p-2 rounded-md bg-surface dark:bg-surface-dark text-primary dark:text-primary-dark border-default dark:border-default-dark"
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setIsEditingSecret(false)}
                        className="px-4 py-2 text-secondary hover:text-secondary-hover dark:text-secondary-dark dark:hover:text-secondary-hover-dark"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleSaveSecret(airport.secretInformation)}
                        className="px-4 py-2 bg-button text-white rounded-md hover:bg-button-hover dark:bg-button-dark dark:hover:bg-button-hover-dark"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <pre className="whitespace-pre-wrap font-sans text-primary dark:text-primary-dark">
                      {airport.secretInformation}
                    </pre>
                    <div className="mt-4 text-sm text-secondary dark:text-secondary-dark">
                      Last modified: {new Date(airport.secretLastModified || airport.dateModified).toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'numeric',
                        day: 'numeric'
                      })}
                    </div>
                  </>
                )}
              </div>
            </section>

            {/* Services */}
            <section>
              <div className="flex justify-between items-center mb-3">
                <h2 className="text-xl font-bold text-primary dark:text-primary-dark">
                  Services
                </h2>
                <button
                  onClick={() => setEditingService({
                    name: '',
                    phone: '',
                    hours: '',
                    services: [],
                    dateModified: new Date().toISOString()
                  })}
                  className={`
                    px-4 py-2 bg-button text-white rounded-md hover:bg-button-hover dark:bg-button-dark dark:hover:bg-button-hover-dark flex items-center gap-2
                    ${!currentUser?.profile?.isEditor ? 'hidden' : ''}
                  `}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                    <path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z" />
                  </svg>
                  Add Company
                </button>
              </div>
              {editingService ? (
                <div className="bg-surface dark:bg-surface-dark rounded-lg shadow-md p-4">
                  <ServiceEditForm
                    service={editingService}
                    onSave={handleSaveService}
                    onCancel={() => setEditingService(null)}
                    onDelete={() => handleDeleteService(editingService.name)}
                  />
                </div>
              ) : (
                (airport.services || []).map((service, index) => (
                  <ServiceCard 
                    key={index} 
                    service={service} 
                    onEdit={setEditingService}
                  />
                ))
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default AirportDetail; 